import { useState } from "react";
import { type FieldValues, type UseFormSetValue } from "react-hook-form";

import { Button, Card, ComboBox, Item } from "@icg360/design-system";

import { type KeystoneMortgageeInfo } from "data/keystone-api-mapping";
import { useKeystone } from "data/ss-data-hooks";
import { isError } from "data/ss-error";

import { type EditMortgageeModalFields } from ".";
import styles from "./edit-mortgagee-modal.module.scss";
import {
  ADD_MY_OWN_ITEM,
  MortgageeItem,
  NO_RESULTS_ITEM,
  TYPE_TO_SEARCH_ITEM,
} from "./mortgagee-Item";

export const DISABLED_INDEXES = [
  NO_RESULTS_ITEM.index,
  TYPE_TO_SEARCH_ITEM.index,
];

type MortgageeSearchProps<T extends FieldValues> = {
  onAddMyOwn: () => void;
  setValue: UseFormSetValue<T>;
};

export const MortgageeSearch = ({
  onAddMyOwn,
  setValue,
}: MortgageeSearchProps<EditMortgageeModalFields>) => {
  const keystone = useKeystone();
  const [mortgageeOptions, setMortgageeOptions] = useState<
    KeystoneMortgageeInfo[]
  >([TYPE_TO_SEARCH_ITEM]);
  const [selectedMortgagee, setSelectedMortgagee] =
    useState<KeystoneMortgageeInfo | null>(null);

  const onMortgageSearchChange = async (searchTerm: string) => {
    if (searchTerm.length > 2) {
      const mortgageeResults = await keystone.mortgageeSearch(searchTerm);
      if (isError(mortgageeResults)) {
        // TODO... some error handling
        return;
      }
      setMortgageeOptions([
        ...(mortgageeResults.length ? mortgageeResults : [NO_RESULTS_ITEM]),
        ADD_MY_OWN_ITEM,
      ]);
    } else {
      setMortgageeOptions([TYPE_TO_SEARCH_ITEM]);
    }
  };

  const onMortgageeSelection = (selectedIndex) => {
    if (selectedIndex === ADD_MY_OWN_ITEM.index) {
      onAddMyOwn();
      return;
    }
    const selectedMortgagee = mortgageeOptions?.find(
      ({ index }) => index === selectedIndex,
    );
    setSelectedMortgagee(selectedMortgagee ?? null);
    setMortgageeOptions([TYPE_TO_SEARCH_ITEM]);
    setValue("name", selectedMortgagee?.name ?? "");
    setValue("street1", selectedMortgagee?.addressLine1 ?? "");
    setValue("city", selectedMortgagee?.city ?? "");
    setValue("state", selectedMortgagee?.state ?? "");
    setValue("zip", selectedMortgagee?.zipCode ?? "");
  };

  return selectedMortgagee ? (
    <Card className={styles.selectedMortgageeCardDS}>
      <MortgageeItem mortgagee={selectedMortgagee} />
      <Button
        icon="Clear"
        appearance="tertiary"
        onPress={() => setSelectedMortgagee(null)}
      />
    </Card>
  ) : (
    <div className={styles.searchWrapper}>
      <ComboBox
        label="Mortgage company"
        placeholder="Search for a mortgage company"
        items={mortgageeOptions ?? []}
        onInputChange={onMortgageSearchChange}
        onSelectionChange={onMortgageeSelection}
        disabledKeys={DISABLED_INDEXES}
        fluid
      >
        {(mortgagee) => (
          <Item key={mortgagee.index} textValue={mortgagee.index}>
            <MortgageeItem mortgagee={mortgagee} />
          </Item>
        )}
      </ComboBox>
    </div>
  );
};
